// React
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { validateStrongPassword } from "utils/utils";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { formBuilderParams } from "components/FormBuilder/FormBuilder";
import { formRulesBuilder } from "components/FormBuilder/lib/FormBuilderRules";
import merchantManagement from "routes/merchantManagement";
import StrongPasswordPopper from "components/StrongPasswordPopper/StrongPasswordPopper";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { extractLoginPrefix } from "../utils/stripLoginPrefix";
import { useMerchantOptionsCache } from "utils/optionsCache/merchantOptionsCache";

const PAGE_LOCALE_KEY = "page.employees.";
const FORM_LABEL_KEY = PAGE_LOCALE_KEY + "employeeDetail.detail.fields";

const EmployeeInfo = ({
  formProps,
  isEdit,
  isLoading,
  redirectFn,
  isCreate = false,
  defaultMerchant = null,
  preferredLoginPrefix = "",
  onMerchantChange,
}) => {
  const { t } = useTranslation();
  const { setValue, register } = formProps;
  const { fetchMerchantOptions } = useMerchantOptionsCache();

  const isDisabled = !isEdit || isLoading;
  const [passwordAnchorEl, setPasswordAnchorEl] = useState(null);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const passwordValue = useWatch({
    control: formProps.control,
    name: "password",
  });
  const loginNameValue = useWatch({
    control: formProps.control,
    name: "email",
  });

  const [selectedMerchant, setSelectedMerchant] = useState(defaultMerchant);
  const [localMerchantList, setLocalMerchantList] = useState([]);
  const [hasMoreMerchants, setHasMoreMerchants] = useState(false);
  const [missingPayfacPrefix, setMissingPayfacPrefix] = useState(false);
  const [inferredLoginPrefix, setInferredLoginPrefix] = useState("");
  const effectiveLoginPrefix = preferredLoginPrefix || inferredLoginPrefix;
  const loginNamePrefix = effectiveLoginPrefix ? `${effectiveLoginPrefix}-` : "";
  const loginFieldLabel = t(`${FORM_LABEL_KEY}.email`);

  useEffect(() => {
    if (isCreate) {
      register("merchantId", { required: true });
    }
  }, [isCreate, register]);

  useEffect(() => {
    if (isCreate || preferredLoginPrefix || isEdit) return;
    setInferredLoginPrefix(extractLoginPrefix(loginNameValue));
  }, [isCreate, isEdit, loginNameValue, preferredLoginPrefix]);

  // Initial fetch for local filtering
  useEffect(() => {
    if (!isCreate) return;
    const fetchAllMerchants = async () => {
      try {
        const { options, hasMoreOptions } = await fetchMerchantOptions();
        setLocalMerchantList(options);
        setHasMoreMerchants(hasMoreOptions);
      } catch (error) {
        console.error("Failed to fetch merchant list", error);
      }
    };

    fetchAllMerchants();
  }, [fetchMerchantOptions, isCreate]);

  const effectiveMerchantList = React.useMemo(() => {
    const list = localMerchantList;
    if (
      defaultMerchant &&
      !list.some((m) => `${m.value}` === `${defaultMerchant.value}`)
    ) {
      return [defaultMerchant, ...list];
    }
    return list;
  }, [localMerchantList, defaultMerchant]);

  useEffect(() => {
    if (!defaultMerchant) return;

    const sameIdMerchants = effectiveMerchantList.filter(
      (m) => `${m.value}` === `${defaultMerchant.value}`,
    );
    const resolvedMerchant =
      sameIdMerchants.find((m) => `${m?.payfacPrefix || ""}`.trim()) ||
      sameIdMerchants[0] ||
      defaultMerchant;

    setSelectedMerchant(resolvedMerchant);
    setValue("merchantId", resolvedMerchant.value, { shouldValidate: true });
    setMissingPayfacPrefix(!!resolvedMerchant && !resolvedMerchant.payfacPrefix);
    onMerchantChange?.(resolvedMerchant);
  }, [defaultMerchant, effectiveMerchantList, onMerchantChange, setValue]);

  const loadMerchantOptions = useCallback(async (keyword) => {
    if (typeof keyword !== "string") return [];
    const trimmed = keyword.trim();
    if (!trimmed) return [];

    try {
      const { options } = await fetchMerchantOptions({ keyword: trimmed });
      return options;
    } catch (error) {
      console.error("Failed to search merchant list", error);
      return [];
    }
  }, [fetchMerchantOptions]);

  const handleMerchantChange = (event, newValue) => {
    setSelectedMerchant(newValue);
    if (newValue) {
      setValue("merchantId", newValue.value, { shouldValidate: true });
      setMissingPayfacPrefix(!!newValue && !newValue.payfacPrefix);
    } else {
      setValue("merchantId", null, { shouldValidate: true });
      setMissingPayfacPrefix(false);
    }
    if (onMerchantChange) {
      onMerchantChange(newValue);
    }
  };

  const navigateToMerchantDetail = (merchantId, redirectFn) => {
    const path = "/merchant-management/merchants/offline-merchant-detail";
    const payload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Merchants")
          .page.find((page) => page.path === path).name + merchantId,
      path: path,
      state: {
        merchantId: merchantId,
        redirectFn: redirectFn,
      },
    };
    redirectFn("redirectPage", payload);
  };

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    firstName: {
      fieldType: "textInput",
      fieldName: "firstname",
      label: t(`${FORM_LABEL_KEY}.firstName`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 50,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ],
      },
    },
    lastName: {
      fieldType: "textInput",
      fieldName: "lastname",
      label: t(`${FORM_LABEL_KEY}.lastName`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ],
      },
    },
    role: {
      fieldType: "selection",
      fieldName: "role",
      label: t(`${FORM_LABEL_KEY}.role`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        defaultValue: "USER",
        options: ["ADMIN", "USER"].map((item) => ({
          key: item,
          title: item,
          value: item,
        })),
      },
    },
    merchantIdDisplay: {
      fieldType: "textDisplay",
      fieldName: "merchantId",
      label: t(`${FORM_LABEL_KEY}.merchantId`),
      config: {
        onClick: (value) => {
          navigateToMerchantDetail(value, redirectFn);
        },
      },
    },
    terminalSerialNumber: {
      fieldType: "textDisplay",
      fieldName: "terminalSerialNumber",
      label: t(`${FORM_LABEL_KEY}.terminalSerialNumber`),
    },
    isBoxsPay: {
      fieldType: "checkbox",
      fieldName: "isBoxsPay",
      label: t(`${FORM_LABEL_KEY}.isBoxsPay`),
      disabled: true, // BPay is not editable
    },
    password: {
      fieldType: "textInput",
      fieldName: "password",
      label: t(`${FORM_LABEL_KEY}.password`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "password",
        customRule: {
          rule: validateStrongPassword,
          errorMessage: t(`${PAGE_LOCALE_KEY}createEmployee.passwordStrength`),
        },
        customOnFocusFn: (e) => {
          setPasswordAnchorEl(e.target);
          setIsPasswordFocused(true);
        },
        customOnBlurFn: () => {
          setIsPasswordFocused(false);
        },
      },
    },
    confirmPassword: {
      fieldType: "textInput",
      fieldName: "confirmPassword",
      label: t(`${FORM_LABEL_KEY}.confirmPassword`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "password",
        customRule: {
          rule: (value, { password }) => value === password,
          errorMessage: t(`${PAGE_LOCALE_KEY}createEmployee.passwordMismatch`),
        },
      },
    },
    status: {
      fieldType: "selection",
      fieldName: "status",
      label: t(`${FORM_LABEL_KEY}.status`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        defaultValue: "ACTIVE",
        options: ["ACTIVE", "SUSPENDED"].map((item) => ({
          key: item,
          title: item,
          value: item,
        })),
      },
    },
  };

  const getLoginNameSuffix = (value) => {
    const stringValue = `${value || ""}`;
    if (!loginNamePrefix) return stringValue;
    if (stringValue.startsWith(loginNamePrefix)) {
      return stringValue.slice(loginNamePrefix.length);
    }
    if (stringValue === effectiveLoginPrefix) return "";
    return stringValue;
  };

  const getLoginFieldDisplayValue = (value) => {
    if (isCreate) {
      return `${value || ""}`;
    }
    return getLoginNameSuffix(value);
  };

  const buildLoginFieldValue = (suffix) => {
    if (isCreate) {
      return suffix;
    }
    return loginNamePrefix ? `${loginNamePrefix}${suffix}` : suffix;
  };

  const loginNamePrefixAttachment = loginNamePrefix ? (
    <div
      style={{
        height: "40px",
        padding: "0 14px",
        border: "1px solid #d0d5dd",
        borderRadius: "4px",
        background: "#f8f9fb",
        color: "#555",
        fontWeight: 600,
        lineHeight: "38px",
        whiteSpace: "nowrap",
        userSelect: "none",
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      {loginNamePrefix}
    </div>
  ) : null;

  const loginNameField = (
    <Controller
      name="email"
      control={formProps.control}
      defaultValue=""
      rules={formRulesBuilder({
        mandatory: true,
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ],
        customRule: {
          rule: (value) => {
            const suffix = getLoginNameSuffix(value).trim();
            return suffix.length > 0;
          },
          errorMessage: "Mandatory",
        },
      })}
      render={({ field: { value, onChange, ref }, fieldState: { error } }) => {
        const errorMessage = missingPayfacPrefix
          ? t(`${PAGE_LOCALE_KEY}createEmployee.missingPayfacPrefix`)
          : error?.message || "";

        return (
          <OutlinedText
            inputRef={ref}
            label={loginFieldLabel}
            mandatory
            value={getLoginFieldDisplayValue(value)}
            onchangeFn={(e) => {
              onChange(buildLoginFieldValue(e.target.value));
            }}
            errorMessage={errorMessage}
            sxParams={{
              textError: !!error || missingPayfacPrefix,
            }}
            divStyle={formBuilderParams.formItemStyle}
            disabled={isDisabled || (isCreate && missingPayfacPrefix)}
            readOnly={false}
            startAttachment={loginNamePrefixAttachment}
          />
        );
      }}
    />
  );

  return (
    <FormBuilder.Section>
      <FormBuilder.Row itemsPerRow={3}>
        {FormBuilder.buildFields({
          formProps,
          fields: isCreate
            ? [fieldsConfig.firstName, fieldsConfig.lastName]
            : [
              fieldsConfig.firstName,
              fieldsConfig.lastName,
              fieldsConfig.role,
              fieldsConfig.merchantIdDisplay,
              fieldsConfig.terminalSerialNumber,
            ],
        })}
        {loginNameField}
        {isCreate &&
          FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.password,
              fieldsConfig.confirmPassword,
              fieldsConfig.role,
              fieldsConfig.status,
            ],
          })}
        {isCreate && (
          <div style={formBuilderParams.formItemStyle}>
            <AsyncAutocomplete
              label={t(`${FORM_LABEL_KEY}.merchantId`)}
              required
              fetchOptions={loadMerchantOptions}
              preloadedOptions={effectiveMerchantList}
              useLocalFilter
              hasMoreOptions={hasMoreMerchants}
              numericExactMatch
              value={selectedMerchant}
              onChange={handleMerchantChange}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              placeholder={t(`${PAGE_LOCALE_KEY}merchantSearchPlaceholder`, "Search Merchant ID or Name")}
              disabled={isDisabled}
              error={!!formProps.formState.errors?.merchantId}
            />
          </div>
        )}
      </FormBuilder.Row>
      {isCreate && (
        <StrongPasswordPopper
          open={isPasswordFocused}
          anchorEl={passwordAnchorEl}
          password={passwordValue}
        />
      )}
    </FormBuilder.Section>
  );
};

export default React.memo(EmployeeInfo);
